import {Button, Link, SelectField, SelectFieldItem, view} from '@sail/ui';
import {LightCard, MetaRow} from '@stripe-internal/conversations-ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {safeWindowOpen} from '@stripe-internal/safe-links';
import {useCallback, useMemo, useState} from 'react';
import {FormattedMessage, useIntl, defineMessages} from 'react-intl';

const SUPPORT_CENTER_URL = 'https://dashboard.stripe.com/support';

const messages = defineMessages({
  title: {
    id: 'mse_inline_data.platform_chooser.title',
    description: 'Title displayed in the platform chooser card.',
    defaultMessage: 'Select platform',
  },
  selectLabel: {
    id: 'mse_inline_data.platform_chooser.select_label',
    description: 'Label for the platform selection dropdown.',
    defaultMessage: 'Platform',
  },
  chooseOneOption: {
    id: 'mse_inline_data.platform_chooser.choose_one_option',
    description: 'Placeholder for the platform selection dropdown.',
    defaultMessage: 'Choose an option',
  },
  skipButton: {
    id: 'mse_inline_data.platform_chooser.skip_button',
    description: 'Button to skip selecting a platform.',
    defaultMessage: 'Skip',
  },
  shareCaseButton: {
    id: 'mse_inline_data.platform_chooser.share_case_button',
    description: 'Button to share the case with the selected platform.',
    defaultMessage: 'Share case',
  },
  confirmation: {
    id: 'mse_inline_data.platform_chooser.confirmation',
    description:
      'Confirmation shown after the user shares the case with a platform.',
    defaultMessage:
      "We'll share your case and any updates with {platformName}. If you want to disable and stop sharing at any time, you can do so in your <link>Support center</link>.",
  },
  skippedLogMessage: {
    id: 'mse_inline_data.platform_chooser.skipped_log_message',
    description: 'Log message shown when the user skips platform selection.',
    defaultMessage: 'Platform selection skipped',
  },
  noPlatformsAvailable: {
    id: 'mse_inline_data.platform_chooser.no_platforms_available',
    description:
      'Shown when there are no platforms available to share the case with.',
    defaultMessage: 'There are no platforms available to share this case with.',
  },
});

export type Platform = {
  token: string;
  name: string;
};

export type ValueProps = {
  platforms?: Platform[];
  selected_platform?: string | null;
};

export const PlatformChooser = (props: InlineDatumReactComponentProps) => {
  const {datum, onDone, isDone} = props;
  const value = datum.value as ValueProps;
  const {platforms, selected_platform: selectedPlatformToken} = value;

  const intl = useIntl();
  const analytics = useAnalytics();

  const [selectedToken, setSelectedToken] = useState<string | undefined>(
    selectedPlatformToken || undefined,
  );
  const [isFinished, setIsFinished] = useState<boolean>(isDone || false);
  const [isSkipped, setIsSkipped] = useState<boolean>(
    Boolean(isDone) && !selectedPlatformToken,
  );

  const availablePlatforms = useMemo(() => platforms || [], [platforms]);

  const selectedPlatform = useMemo(
    () => availablePlatforms.find((p) => p.token === selectedToken),
    [availablePlatforms, selectedToken],
  );

  const pageState = useMemo(
    () => ({
      hasPlatforms: availablePlatforms.length > 0,
      hasSelection: Boolean(selectedToken),
    }),
    [availablePlatforms.length, selectedToken],
  );

  const sharePressed = useCallback(() => {
    if (!selectedToken) {
      return;
    }
    onDone({
      type: 'success',
      message: selectedPlatform?.name,
      extra: {
        outcome: selectedToken,
        value,
      },
    });
    setIsFinished(true);
    analytics.action('platform_chooser_share_click', pageState);
  }, [
    analytics,
    onDone,
    pageState,
    selectedPlatform?.name,
    selectedToken,
    value,
  ]);

  const skipPressed = useCallback(() => {
    onDone({
      type: 'success',
      message: '',
      extra: {
        outcome: '',
      },
    });
    setIsSkipped(true);
    setIsFinished(true);
    analytics.action('platform_chooser_skip_click', pageState);
  }, [analytics, onDone, pageState]);

  if (isSkipped) {
    return (
      <view.div
        css={{margin: 'medium'}}
        data-testid="skip-platform-chooser-meta-row"
      >
        <MetaRow>{intl.formatMessage(messages.skippedLogMessage)}</MetaRow>
      </view.div>
    );
  }

  if (isFinished) {
    return (
      <view.div css={{margin: 'medium'}} data-testid="platform-chooser-done">
        <MetaRow>
          <FormattedMessage
            {...messages.confirmation}
            values={{
              platformName: <b>{selectedPlatform?.name}</b>,
              link: (chunks) => (
                <Link
                  onPress={() => {
                    safeWindowOpen(SUPPORT_CENTER_URL, '_blank');
                  }}
                >
                  {chunks}
                </Link>
              ),
            }}
          />
        </MetaRow>
      </view.div>
    );
  }

  if (availablePlatforms.length === 0) {
    return (
      <view.div css={{margin: 'medium'}} data-testid="no-platforms-available">
        <MetaRow>{intl.formatMessage(messages.noPlatformsAvailable)}</MetaRow>
      </view.div>
    );
  }

  return (
    <view.div css={{marginTop: 'small'}}>
      <LightCard isOuter>
        <view.div css={{gap: 'medium', stack: 'y'}}>
          <view.div css={{margin: 0, font: 'heading.small'}}>
            {intl.formatMessage(messages.title)}
          </view.div>

          <SelectField
            css={{minWidth: 'fill'}}
            data-testid="platform-select"
            label={intl.formatMessage(messages.selectLabel)}
            placeholder={intl.formatMessage(messages.chooseOneOption)}
            selectedKey={selectedToken}
            onSelectionChange={(token) => {
              setSelectedToken(token as string);
            }}
          >
            {availablePlatforms.map((platform) => (
              <SelectFieldItem key={platform.token} id={platform.token}>
                {platform.name}
              </SelectFieldItem>
            ))}
          </SelectField>

          <view.div css={{gap: 'small', stack: 'x'}}>
            <Button
              css={{width: 'fill'}}
              data-testid="skip-btn"
              onPress={skipPressed}
            >
              <FormattedMessage {...messages.skipButton} />
            </Button>
            <Button
              css={{width: 'fill'}}
              data-testid="share-case-btn"
              type="primary"
              onPress={sharePressed}
              disabled={!selectedToken}
            >
              <FormattedMessage {...messages.shareCaseButton} />
            </Button>
          </view.div>
        </view.div>
      </LightCard>
    </view.div>
  );
};
